/**
 * The service worker's routing table, as pure predicates over a URL and a method.
 *
 * `sw.ts` is untestable in this repo (no jsdom, no service-worker harness) so it stays thin:
 * it wires Workbox strategies to these functions and does nothing else. Everything that could
 * be *wrong* — which reads may be served from a cache, which must never be — lives here, where
 * `sw-routes.test.ts` can hold it still.
 *
 * The bias throughout is deny-by-default. A route is cacheable only because it is named below;
 * a new API route added later is network-only until someone deliberately adds it, which is the
 * safe direction to fail in. The event log is the product (M5 rule 1): nothing that writes, and
 * nothing whose staleness could be mistaken for a fact about the learner, is cached.
 */

/** Same-origin API reads that make an offline open of the installed app useful. */
const CACHEABLE_API_PREFIXES = [
  '/api/saved', // the saved list — Words offline
  '/api/items', // item views, proposals, per-item lessons
  '/api/lessons',
  '/api/coverage',
] as const;

/**
 * Exact pathnames (any query string). `/api/videos` is here rather than in the prefix list on
 * purpose: the Library always sends `?scope=`, so the *list* is worth caching, but
 * `/api/videos/{id}` is Watch's payload and Watch is online-only — a prefix match would have
 * quietly promised an offline Watch the app cannot deliver.
 */
const CACHEABLE_API_EXACT = ['/api/me', '/api/today/summary', '/api/videos'] as const;

/**
 * Paths the navigation fallback must not swallow. Matched against pathname + search so that
 * better-auth's `/api/auth/*` navigations (the magic-link and OAuth round trips, which ARE
 * top-level navigations) reach the network instead of being answered with the app shell.
 */
export const NAV_DENYLIST: RegExp[] = [/^\/api\//, /^\/health/, /^\/__scheduled/];

/** The predicate form of NAV_DENYLIST — the same array Workbox's NavigationRoute is handed. */
export function isDenied(pathnameAndSearch: string): boolean {
  return NAV_DENYLIST.some((re) => re.test(pathnameAndSearch));
}

/** Google Fonts stylesheet — stale-while-revalidate: the offline shell is unstyled without it. */
export function isFontCss(url: URL): boolean {
  return url.origin === 'https://fonts.googleapis.com';
}

/** The font files themselves — immutable, cache-first, long-lived. */
export function isFontFile(url: URL): boolean {
  return url.origin === 'https://fonts.gstatic.com';
}

/** `GET /api/tts/{ttsId}/audio` — the only media this app caches. Same-origin only. */
export function isTtsAudio(url: URL, method: string, origin: string): boolean {
  if (method !== 'GET' || url.origin !== origin) return false;
  return /^\/api\/tts\/[^/]+\/audio$/.test(url.pathname);
}

/**
 * A same-origin API read worth keeping a copy of. Never a write, never a different origin, and
 * never one of the routes whose whole value is being live (events, sessions, activities, gloss,
 * jobs, curator, media ranges, transcripts, or a single video).
 */
export function isCacheableApiRead(url: URL, method: string, origin: string): boolean {
  if (method !== 'GET' || url.origin !== origin) return false;
  const p = url.pathname;
  if ((CACHEABLE_API_EXACT as readonly string[]).includes(p)) return true;
  return CACHEABLE_API_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/** The two caches that belong to one signed-in learner, and are purged when that changes. */
export const USER_CACHES = ['nn-api', 'nn-tts'] as const;
export const FONT_CACHES = ['nn-fonts-css', 'nn-fonts'] as const;
